import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

// SINGLE-SESSION ENFORCEMENT (same-browser).
// Cookies are shared across tabs of the same browser, so the server-side jti
// check alone can't kick out a sibling tab — when Tab B signs in, its new
// cookies overwrite Tab A's, and Tab A silently inherits the new session.
// To make the kick-out feel real, every tab broadcasts on login via
// `BroadcastChannel`, and all other tabs of the same origin self-eject.
const SESSION_CHANNEL = "adn-auth-session";
// Per-tab UUID (sessionStorage IS scoped per-tab, unlike cookies/localStorage).
function getTabId() {
    try {
        let id = sessionStorage.getItem("adn_tab_id");
        if (!id) {
            id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            sessionStorage.setItem("adn_tab_id", id);
        }
        return id;
    } catch { return `${Date.now()}-${Math.random()}`; }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const tabIdRef = useRef(getTabId());
    const channelRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me", { validateStatus: (s) => (s >= 200 && s < 300) || s === 401 });
            if (data && data.id) setUser(data); else setUser(false);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Cross-tab session listener: when ANY tab successfully signs in,
    // it broadcasts {type:"session_started", tab_id, user_id, role}. Other
    // tabs of the same browser receive it and — if the role is NOT admin
    // and the user_id matches what they have or they're currently signed
    // in — eject themselves to /login?reason=session_revoked.
    useEffect(() => {
        if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return;
        const ch = new BroadcastChannel(SESSION_CHANNEL);
        channelRef.current = ch;
        ch.onmessage = (ev) => {
            const m = ev.data || {};
            if (m.type !== "session_started") return;
            if (m.tab_id === tabIdRef.current) return;          // ignore self-echo
            if (m.role === "admin") return;                     // admins multi-session
            // Eject this tab. Clear local auth, toast, redirect.
            (async () => {
                try {
                    const { toast } = await import("sonner");
                    toast.error("You've been signed in from another tab or device. Please sign in again.", { duration: 8000 });
                } catch { /* ignore */ }
                setUser(false);
                setTimeout(() => {
                    try { window.location.assign("/login?reason=session_revoked"); }
                    catch { window.location.href = "/login"; }
                }, 600);
            })();
        };
        return () => {
            try { ch.close(); } catch { /* ignore */ }
            channelRef.current = null;
        };
    }, []);

    const broadcastSessionStart = (u) => {
        try {
            channelRef.current?.postMessage({
                type: "session_started",
                tab_id: tabIdRef.current,
                user_id: u?.id,
                role: u?.role,
                at: Date.now(),
            });
        } catch { /* ignore */ }
    };

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        broadcastSessionStart(data);
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setUser(data);
        broadcastSessionStart(data);
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch { /* ignore */ }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, formatApiErrorDetail }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
